// Configuration SEO par route (titres, meta, images de partage).
//
// Chaque route publique importante possède un titre, une description et des
// mots-clés cohérents avec son contenu. Les espaces authentifiés (propriétaire,
// agence, super admin) sont marqués `noindex` : ils restent accessibles mais ne
// doivent pas être indexés. Aucun contenu commercial n'est modifié ici : ce
// fichier ne fait que décrire les métadonnées.
//
// Source de vérité des métadonnées Head :
//   1. données SEO autoritaires (fournies explicitement)
//   2. valeurs Open Graph / Twitter gérées par la plateforme
//   3. données SEO du site déjà présentes (ce fichier)
//   4. repli raisonnable généré

import { BRAND } from "@/config/brand";

/**
 * Image de partage par défaut : favicon fourni par l'utilisateur (seule URL
 * d'image absolue valide disponible dans le projet). Jamais inventée.
 */
export const SEO_IMAGE =
  "https://static.readdy.ai/image/c7d960c7be6f89705ef351d7ef00f6c3/c90976b79bd88ecdd5bf61b0bd3040b2.png";

export interface SeoEntry {
  title: string;
  description: string;
  /** Mots-clés séparés par des virgules. */
  keywords?: string;
  /** Directives robots ; par défaut « index, follow ». */
  robots?: string;
  /** Type Open Graph ; par défaut « website ». */
  ogType?: string;
}

/** Origine réelle du site (variable d'environnement, sinon origine à l'exécution). */
export function getSiteOrigin(): string {
  const raw = import.meta.env.VITE_SITE_URL as string | undefined;
  if (raw && raw.trim()) return raw.trim().replace(/\/+$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return BRAND.domain;
}

/** Métadonnées globales de repli (identiques au <head> d'index.html). */
export const DEFAULT_SEO: SeoEntry = {
  title: "ImmoBF — Gestion immobilière au Burkina Faso",
  description:
    "ImmoBF est le logiciel de gestion immobilière pensé pour le Burkina Faso : immeubles, biens, locataires, contrats, loyers, paiements et impayés, pour les propriétaires et les agences immobilières.",
  keywords:
    "gestion immobilière, logiciel immobilier Burkina Faso, immeubles, biens, locataires, contrats, loyers, paiements, impayés, FCFA, propriétaires, agences immobilières",
};

/**
 * Repli pour toute route inconnue (= page 404) : jamais indexée.
 * Distinct de DEFAULT_SEO, qui reste indexable et sert la page d'accueil "/".
 */
export const NOT_FOUND_SEO: SeoEntry = {
  title: DEFAULT_SEO.title,
  description: DEFAULT_SEO.description,
  robots: "noindex, nofollow",
};

const PUBLIC_SEO: Record<string, SeoEntry> = {
  "/": DEFAULT_SEO,
  "/fonctionnalites": {
    title: "Fonctionnalités — Gestion immobilière ImmoBF",
    description:
      "Découvrez les fonctionnalités d'ImmoBF : suivi des immeubles et biens, gestion des locataires, contrats, loyers, paiements, impayés, dépenses, maintenance et documents.",
    keywords:
      "fonctionnalités gestion immobilière, logiciel locatif Burkina Faso, immeubles, biens, locataires, contrats, loyers, paiements, impayés, documents",
  },
  "/comment-ca-marche": {
    title: "Comment ça marche — ImmoBF",
    description:
      "Comment ImmoBF simplifie la gestion locative au Burkina Faso : créez votre compte, ajoutez vos immeubles et biens, suivez vos loyers et vos impayés en quelques minutes.",
    keywords:
      "comment ça marche, gestion locative, logiciel immobilier Burkina Faso, immeubles, loyers, impayés",
  },
  "/solutions": {
    title: "Solutions pour propriétaires et agences — ImmoBF",
    description:
      "ImmoBF propose une solution adaptée aux propriétaires bailleurs comme aux agences immobilières : gestion multi-propriétaires, employés, rapports et statistiques.",
    keywords:
      "solutions propriétaires, agences immobilières, gestion multi-propriétaires, employés, rapports, statistiques, Burkina Faso",
  },
  "/tarifs": {
    title: "Tarifs et formules d'abonnement — ImmoBF",
    description:
      "Comparez les formules ImmoBF (Gratuit, Essentiel, Professionnel, Agence) et choisissez celle adaptée à votre patrimoine. Tarifs en FCFA, mensuels ou annuels.",
    keywords:
      "tarifs ImmoBF, formules abonnement, logiciel immobilier FCFA, prix gestion locative, propriétaire, agence",
  },
  "/faq": {
    title: "Questions fréquentes — ImmoBF",
    description:
      "Réponses aux questions fréquentes sur ImmoBF : création de compte, formules d'abonnement, gestion des loyers, sécurité des données et utilisation au Burkina Faso.",
    keywords:
      "FAQ ImmoBF, questions fréquentes, gestion immobilière, abonnement, loyers, sécurité des données, Burkina Faso",
  },
  "/a-propos": {
    title: "À propos — ImmoBF",
    description:
      "ImmoBF est une solution de gestion immobilière conçue pour le marché burkinabè : propriétaires, agences, immeubles, biens, locataires, contrats et loyers.",
    keywords:
      "à propos ImmoBF, logiciel immobilier Burkina Faso, mission, propriétaires, agences immobilières",
  },
  "/contact": {
    title: "Contact — ImmoBF",
    description:
      "Contactez l'équipe ImmoBF pour toute question sur la gestion immobilière, les formules d'abonnement ou l'accompagnement de votre agence.",
    keywords:
      "contact ImmoBF, support, assistance, Ouagadougou, gestion immobilière Burkina Faso",
  },
  "/connexion": {
    title: "Connexion — ImmoBF",
    description:
      "Connectez-vous à votre espace ImmoBF (propriétaire ou agence) pour gérer vos immeubles, biens, locataires, contrats, loyers et paiements.",
    keywords: "connexion ImmoBF, espace propriétaire, espace agence, gestion immobilière",
    robots: "noindex, nofollow",
  },
  "/reinitialiser-mot-de-passe": {
    title: "Définir un nouveau mot de passe — ImmoBF",
    description:
      "Définissez ou réinitialisez le mot de passe de votre compte ImmoBF via un lien sécurisé.",
    robots: "noindex, nofollow",
  },
  "/inscription": {
    title: "Créer un compte — ImmoBF",
    description:
      "Créez gratuitement votre compte ImmoBF (propriétaire ou agence) et commencez à gérer vos biens, locataires, contrats et loyers au Burkina Faso.",
    keywords:
      "inscription ImmoBF, créer un compte, propriétaire, agence immobilière, gestion locative Burkina Faso",
    robots: "noindex, nofollow",
  },
  "/abonnement": {
    title: "Abonnement — ImmoBF",
    description:
      "Choisissez ou renouvelez votre formule d'abonnement ImmoBF : Gratuit, Essentiel, Professionnel ou Agence, en paiement mensuel ou annuel.",
    keywords:
      "abonnement ImmoBF, formule, paiement FCFA, gestion immobilière, propriétaire, agence",
    robots: "noindex, nofollow",
  },
  "/finaliser-paiement": {
    title: "Finaliser le paiement — ImmoBF",
    description: "Finalisation sécurisée du paiement d'un abonnement ImmoBF.",
    robots: "noindex, nofollow",
  },
  "/403": {
    title: "Accès refusé — ImmoBF",
    robots: "noindex, nofollow",
  },
  "/500": {
    title: "Erreur serveur — ImmoBF",
    robots: "noindex, nofollow",
  },
  "/maintenance": {
    title: "Maintenance — ImmoBF",
    robots: "noindex, nofollow",
  },
  "/mentions-legales": {
    title: "Mentions légales — ImmoBF",
    description:
      "Mentions légales d'ImmoBF, plateforme logicielle de gestion immobilière pour les propriétaires et les agences au Burkina Faso.",
    keywords: "mentions légales ImmoBF, éditeur, plateforme, gestion immobilière Burkina Faso",
  },
  "/confidentialite": {
    title: "Politique de confidentialité — ImmoBF",
    description:
      "Données traitées, finalités, sécurité et droits des utilisateurs sur ImmoBF, plateforme de gestion immobilière au Burkina Faso.",
    keywords:
      "politique de confidentialité, données personnelles, sécurité, ImmoBF, gestion immobilière",
  },
  "/cgu": {
    title: "Conditions générales d'utilisation — ImmoBF",
    description:
      "Règles d'accès et d'utilisation de la plateforme ImmoBF : comptes, rôles, obligations et responsabilités des utilisateurs.",
    keywords: "CGU ImmoBF, conditions d'utilisation, comptes, rôles, responsabilités",
  },
  "/cgv": {
    title: "Conditions générales de vente — ImmoBF",
    description:
      "Conditions générales de vente des abonnements ImmoBF : formules, tarifs en FCFA, périodicité, paiement et expiration.",
    keywords: "CGV ImmoBF, conditions de vente, abonnements, tarifs FCFA, paiement",
  },
  "/cookies": {
    title: "Politique cookies — ImmoBF",
    description:
      "Cookies et stockages utilisés par ImmoBF : éléments nécessaires à l'authentification, à la session et au fonctionnement du service.",
    keywords: "cookies, politique cookies, session, authentification, ImmoBF",
  },
};

const PRIVATE_PREFIXES: Array<{ prefix: string; entry: SeoEntry }> = [
  {
    prefix: "/proprietaire",
    entry: {
      title: "Espace Propriétaire — ImmoBF",
      description: "Espace de gestion réservé aux propriétaires ImmoBF.",
      robots: "noindex, nofollow",
    },
  },
  {
    prefix: "/agence",
    entry: {
      title: "Espace Agence — ImmoBF",
      description: "Espace de gestion réservé aux agences immobilières ImmoBF.",
      robots: "noindex, nofollow",
    },
  },
  {
    prefix: "/super-admin",
    entry: {
      title: "Super administration — ImmoBF",
      description: "Espace d'administration de la plateforme ImmoBF.",
      robots: "noindex, nofollow",
    },
  },
];

/** Résout les métadonnées SEO d'une route à partir de son chemin. */
export function resolveSeo(pathname: string): SeoEntry {
  const path = (pathname || "/").split("?")[0].replace(/\/+$/, "") || "/";
  const exact = PUBLIC_SEO[path];
  if (exact) return exact;
  const privateMatch = PRIVATE_PREFIXES.find(
    (rule) => path === rule.prefix || path.startsWith(`${rule.prefix}/`)
  );
  if (privateMatch) return privateMatch.entry;
  return NOT_FOUND_SEO;
}

/** Indique si une route est destinée à l'indexation (pour l'audit SEO). */
export function isIndexable(pathname: string): boolean {
  const path = (pathname || "/").split("?")[0].replace(/\/+$/, "") || "/";
  const privateMatch = PRIVATE_PREFIXES.find(
    (rule) => path === rule.prefix || path.startsWith(`${rule.prefix}/`)
  );
  if (privateMatch) return false;
  const entry = PUBLIC_SEO[path];
  if (!entry) return false;
  return !(entry.robots && entry.robots.includes("noindex"));
}